import { useCallback, useMemo } from 'react';
import { fetchUsers, useGetParcellingAll, useGetProperties } from '@practium/features/api';
import type { BindingUsersResponse, PropDTOResponse, ParcelamentoResponse } from '@practium/features/parcelling';

const EMPTY_LIST: ParcelamentoResponse = {
	docs: [],
	totalDocs: 0,
	limit: 200,
	totalPages: 0,
	page: 1,
	pagingCounter: 1,
	hasPrevPage: false,
	hasNextPage: false,
	prevPage: null,
	nextPage: null,
};

const mapPropDocs = (docs: any[]) =>
	docs.map((p: any) => ({
		_id: p._id,
		name: p.name,
		city: p.address?.city ?? p.city ?? '',
		state: p.address?.state ?? p.state ?? '',
		userId: p.userId ?? '',
	}));

export const useAdminParcellingPage = ({
	page = 1,
	limit = 15,
}: {
	page?: number;
	limit?: number;
} = {}) => {
	const { data: parcellingDocs, pagination: parcellingPagination } = useGetParcellingAll({
		enabled: true,
		page,
		limit,
	});
	const { refetch: refetchProperties } = useGetProperties({ limit: 300, enabled: false });

	const mockData = useMemo<ParcelamentoResponse>(() => {
		if (parcellingDocs.length === 0) return EMPTY_LIST;
		return {
			docs: parcellingDocs,
			totalDocs: parcellingPagination?.totalDocs ?? parcellingDocs.length,
			limit: 200,
			totalPages: parcellingPagination?.totalPages ?? Math.max(1, Math.ceil(parcellingDocs.length / 200)),
			page: parcellingPagination?.page ?? 1,
			pagingCounter: parcellingPagination?.page ?? 1,
			hasPrevPage: parcellingPagination?.hasPrevPage ?? false,
			hasNextPage: parcellingPagination?.hasNextPage ?? false,
			prevPage: parcellingPagination?.prevPage ?? null,
			nextPage: parcellingPagination?.nextPage ?? null,
		};
	}, [parcellingDocs, parcellingPagination]);

	const fetchBindingUsers = useCallback(async (query: string): Promise<BindingUsersResponse> => {
		const result = await fetchUsers({ limit: 200, page: 1, userNameOrEmail: query || undefined });

		const docs = (result.docs ?? []).map((u: any) => ({
			_id: u._id,
			name: u.name,
			surName: u.surName ?? null,
			email: u.email,
			role: u.role ?? '',
			status: String(u.status ?? true),
			avatar: u.profileImage ? { url: u.profileImage } : undefined,
		}));

		return {
			docs,
			pagination: {
				currentPage: 1,
				totalPages: 1,
				totalItems: docs.length,
				itemsPerPage: docs.length,
			},
		};
	}, []);

	const fetchFarms = useCallback(async (): Promise<PropDTOResponse> => {
		const result = await refetchProperties();
		const docs = mapPropDocs(result.data?.docs ?? []);

		return {
			docs,
			pagination: {
				currentPage: 1,
				totalPages: 1,
				totalItems: docs.length,
				itemsPerPage: docs.length,
			},
		};
	}, [refetchProperties]);

	return { mockData, fetchFarms, fetchBindingUsers };
};
